/**
 * 已审商品改价：partialEdit 不可带 cost_info / price_info_list，须按店铺类型走专用接口。
 * - 半托管/全托管：POST /open-api/goods/update-cost
 * - 自运营：POST /open-api/openapi-business-backend/product/price/save
 */
import { temuShopCapabilities } from '@/temu-platform/utils/temuShopCapabilities';
import { coerceSiteCurrencyMap } from '@/temu-platform/utils/temuSiteList';

/** 自运营售价涨价原因（文档枚举） */
export const SALE_PRICE_RISE_REASONS = [
  { code: '1', label: '商品成本上涨' },
  { code: '2', label: '物流履约费用上涨' },
  { code: '3', label: '活动结束恢复价格' },
  { code: '4', label: '其他' },
  { code: '5', label: '物流履约费用上涨（物流规则调整）' },
];

function moneyNum(v) {
  if (v == null || v === '') return null;
  const n = Number(String(v).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : null;
}

function formatMoney(v) {
  const n = moneyNum(v);
  if (n == null) return '';
  return String(n);
}

function skuKey(skc, sku) {
  return [
    String(skc?.skcName || skc?.id || ''),
    String(sku?.skuCode || sku?.id || sku?.supplierSku || ''),
  ].join('::');
}

function findBaselineSku(baselineForm, skc, sku) {
  const baselineSkcs = Array.isArray(baselineForm?.skcList) ? baselineForm.skcList : [];
  const skcName = String(skc?.skcName || '').trim();
  const skuCode = String(sku?.skuCode || '').trim();
  const supplierSku = String(sku?.supplierSku || '').trim();
  const baseSkc = baselineSkcs.find((row) => {
    if (skcName && String(row?.skcName || '').trim() === skcName) return true;
    return false;
  }) || baselineSkcs.find((row) => String(row?.id || '') === String(skc?.id || ''));
  if (!baseSkc) return null;
  const rows = Array.isArray(baseSkc.skuRows) ? baseSkc.skuRows : [];
  return rows.find((row) => {
    if (skuCode && String(row?.skuCode || '').trim() === skuCode) return true;
    if (supplierSku && String(row?.supplierSku || '').trim() === supplierSku) return true;
    return String(row?.id || '') === String(sku?.id || '');
  }) || null;
}

/**
 * 收集已存在 SKU（有平台 skuCode）相对 baseline 的价格变更。
 * 新 SKU（无 skuCode）仍可走 partialEdit 带价，不在此列。
 */
export function collectSkuPriceChanges(form = {}, baselineForm = null, shopTypeOrCaps = null) {
  const caps = shopTypeOrCaps && typeof shopTypeOrCaps === 'object' && 'canUpdateCost' in shopTypeOrCaps
    ? shopTypeOrCaps
    : temuShopCapabilities(shopTypeOrCaps || form?.shopType);
  const items = [];
  const skcList = Array.isArray(form?.skcList) ? form.skcList : [];
  skcList.forEach((skc) => {
    const skcName = String(skc?.skcName || '').trim();
    (skc?.skuRows || []).forEach((sku) => {
      const skuCode = String(sku?.skuCode || '').trim();
      if (!skuCode) return;
      const base = findBaselineSku(baselineForm, skc, sku);
      const nextPrice = moneyNum(sku?.price);
      const prevPrice = moneyNum(base?.price);
      if (nextPrice == null || nextPrice <= 0) return;
      if (prevPrice != null && Math.abs(nextPrice - prevPrice) < 1e-9) return;
      const increased = prevPrice != null && nextPrice > prevPrice;
      items.push({
        key: skuKey(skc, sku),
        spuName: String(form?.spuName || '').trim(),
        skcName,
        skuCode,
        currency: String(sku?.currency || form?.currency || '').trim().toUpperCase(),
        price: nextPrice,
        prevPrice,
        increased,
        specialPrice: sku?.specialPrice != null && sku.specialPrice !== ''
          ? moneyNum(sku.specialPrice)
          : (base?.specialPrice != null && base.specialPrice !== '' ? moneyNum(base.specialPrice) : null),
        colorLabel: skc?.colorLabel || '',
        sizeLabel: sku?.sizeLabel || '',
      });
    });
  });

  const mode = caps.canUpdateSalePrice ? 'sale' : (caps.canUpdateCost ? 'cost' : 'none');
  return {
    mode,
    caps,
    items,
    empty: items.length === 0,
    hasIncrease: items.some((row) => row.increased),
    skcNames: [...new Set(items.map((row) => row.skcName).filter(Boolean))],
  };
}

/** priceLockType=1 强锁不可涨价；0/2 可涨（2 仅提醒） */
export function isPriceRiseBlockedByLock(priceLockType) {
  const n = Number(priceLockType);
  return n === 1;
}

/**
 * 从 searchProduct 结果提取 SKC 站点涨价锁。
 * @returns {Map<string, Array<{site:string, priceLockType:number}>>}
 */
export function extractPriceLocksFromSearch(searchInfo) {
  const map = new Map();
  const root = searchInfo?.info && typeof searchInfo.info === 'object' ? searchInfo.info : searchInfo;
  const dataList = Array.isArray(root?.data)
    ? root.data
    : (Array.isArray(root?.list) ? root.list : (Array.isArray(root) ? root : []));
  dataList.forEach((spu) => {
    const skcList = Array.isArray(spu?.skcList)
      ? spu.skcList
      : (Array.isArray(spu?.skc_list) ? spu.skc_list : []);
    skcList.forEach((skc) => {
      const name = String(skc?.skcName || skc?.skc_name || '').trim();
      if (!name) return;
      const locks = Array.isArray(skc?.activityLockList)
        ? skc.activityLockList
        : (Array.isArray(skc?.activity_lock_list) ? skc.activity_lock_list : []);
      map.set(name, locks.map((row) => ({
        site: String(row?.site || row?.subSite || '').trim(),
        priceLockType: Number(row?.priceLockType ?? row?.price_lock_type ?? 0),
      })));
    });
  });
  return map;
}

/**
 * 涨价前拦截：若任一变更 SKC 在目标站点 priceLockType=1，返回可读错误。
 */
export function validatePriceChangesAgainstLocks(changes, lockMap, sites = []) {
  if (!changes?.hasIncrease || !lockMap || lockMap.size === 0) {
    return { ok: true };
  }
  const siteSet = new Set((sites || []).map((s) => String(s || '').trim()).filter(Boolean));
  const blocked = [];
  changes.skcNames.forEach((skcName) => {
    const locks = lockMap.get(skcName) || [];
    locks.forEach((lock) => {
      if (!isPriceRiseBlockedByLock(lock.priceLockType)) return;
      if (siteSet.size && lock.site && !siteSet.has(lock.site)) return;
      blocked.push(`${skcName}${lock.site ? `@${lock.site}` : ''}`);
    });
  });
  if (!blocked.length) return { ok: true };
  return {
    ok: false,
    message: `以下 SKC 处于营销活动涨价锁定，不可涨价：${blocked.slice(0, 6).join('、')}${blocked.length > 6 ? '…' : ''}`,
  };
}

/** 组装半托管/全托管 update-cost 请求体 */
export function buildUpdateCostPayload(changes, {
  reasonBySkc = {},
  remarkBySkc = {},
  fileBySkc = {},
} = {}) {
  if (!changes?.items?.length) {
    throw new Error('没有成本价变更');
  }
  const spuName = String(changes.items[0]?.spuName || '').trim();
  if (!spuName) {
    throw new Error('缺少平台 SPU，无法更新成本价');
  }
  const bySkc = new Map();
  changes.items.forEach((item) => {
    if (!item.skcName) {
      throw new Error(`SKU ${item.skuCode} 缺少平台 SKC，无法更新成本价`);
    }
    if (!item.currency) {
      throw new Error(`SKU ${item.skuCode} 缺少币种`);
    }
    if (!bySkc.has(item.skcName)) bySkc.set(item.skcName, []);
    bySkc.get(item.skcName).push(item);
  });

  const skcInfoList = [];
  for (const [skcName, skus] of bySkc.entries()) {
    const reason = reasonBySkc[skcName];
    if (reason == null || reason === '') {
      throw new Error(`SKC ${skcName} 请选择涨价原因（平台 update-cost 必填）`);
    }
    const row = {
      skc_name: skcName,
      change_price_reason_flag: String(reason),
      sku_info_list: skus.map((sku) => ({
        cost: Number(formatMoney(sku.price)),
        currency: sku.currency,
        sku_code: sku.skuCode,
        spu_name: spuName,
      })),
    };
    const remark = String(remarkBySkc[skcName] || '').trim();
    if (remark) row.change_remark = remark.slice(0, 1000);
    const file = fileBySkc[skcName];
    if (file?.object_key || file?.objectKey) {
      row.file_upload_list = [{
        object_key: file.object_key || file.objectKey,
        type: String(file.type || '5'),
        url: file.url || undefined,
        file_name: file.file_name || file.fileName || undefined,
      }];
    }
    skcInfoList.push(row);
  }

  // 平台单次最多 20 SKC / 20 SKU：调用方按批切分
  return {
    spu_name: spuName,
    skc_info_list: skcInfoList,
  };
}

/** 组装自运营 product/price/save 请求体 */
export function buildSaveSalePricePayload(changes, {
  sites = [],
  riseReason = '',
  siteList = null,
  siteCurrencyMap = null,
} = {}) {
  if (!changes?.items?.length) {
    throw new Error('没有售价变更');
  }
  const siteListForPrice = (Array.isArray(sites) ? sites : [])
    .map((s) => String(s || '').trim())
    .filter(Boolean);
  if (!siteListForPrice.length) {
    throw new Error('自运营改售价需要站点（子站点，如 temu-us）');
  }
  const currencyMap = coerceSiteCurrencyMap({ siteCurrencyMap, siteList });
  const productPriceList = [];
  changes.items.forEach((item) => {
    siteListForPrice.forEach((site) => {
      const currencyCode = String(currencyMap[site] || item.currency || '').trim().toUpperCase();
      if (!currencyCode) {
        throw new Error(`SKU ${item.skuCode} 缺少站点 ${site} 的币种`);
      }
      const row = {
        productCode: item.skuCode,
        currencyCode,
        shopPrice: Number(formatMoney(item.price)),
        site,
      };
      // 不传 specialPrice 会被平台置 0，必须显式带回
      if (item.specialPrice != null && Number.isFinite(item.specialPrice)) {
        row.specialPrice = Number(formatMoney(item.specialPrice));
      } else {
        row.specialPrice = 0;
      }
      if (item.increased && riseReason) {
        row.riseReason = String(riseReason);
      }
      productPriceList.push(row);
    });
  });
  if (productPriceList.length > 100) {
    throw new Error(`单次售价更新最多 100 条，当前 ${productPriceList.length} 条，请分批`);
  }
  return { productPriceList };
}

export function isEmptyPartialEditError(err) {
  const msg = String(err?.message || err || '');
  return /没有可提交的变更/.test(msg);
}

/** 解析售价/成本价接口结果是否成功 */
export function interpretPriceUpdateResponse(res, mode = 'cost') {
  if (!res || typeof res !== 'object') {
    return { ok: false, message: '改价接口无响应' };
  }
  const code = res.code == null ? '' : String(res.code);
  const gatewayOk = code === '0' || code === '000000' || code === '';
  if (mode === 'sale') {
    const data = Array.isArray(res?.info?.data)
      ? res.info.data
      : (Array.isArray(res?.data) ? res.data : []);
    if (data.length) {
      const failed = data.filter((row) => row && (row.success === false || Number(row.status) === 0));
      const pending = data.filter((row) => Number(row?.status) === 2 || Number(row?.status) === 3);
      if (failed.length) {
        const msg = failed
          .map((row) => row.msg || row.message || `${row.productCode || ''} 失败`)
          .filter(Boolean)
          .slice(0, 5)
          .join('；');
        return { ok: false, message: msg || '售价更新失败', pending: false };
      }
      if (pending.length) {
        return {
          ok: true,
          pending: true,
          message: '售价已提交，部分站点待平台审核，审核通过前请勿重复改价',
        };
      }
    }
    if (!gatewayOk) {
      return {
        ok: false,
        message: res.msg || res.message || '售价更新失败',
      };
    }
    return { ok: true, pending: false, message: '售价已更新' };
  }

  // cost：成功时 info 常为 null
  if (!gatewayOk) {
    return {
      ok: false,
      message: res.msg || res.message || res?.info?.msg || '成本价更新失败',
    };
  }
  return { ok: true, pending: false, message: '成本价已更新' };
}

/** 按 20 SKC 切分 update-cost 请求 */
export function chunkUpdateCostPayload(payload, maxSkc = 20) {
  const list = Array.isArray(payload?.skc_info_list) ? payload.skc_info_list : [];
  if (list.length <= maxSkc) return [payload];
  const chunks = [];
  for (let i = 0; i < list.length; i += maxSkc) {
    chunks.push({
      spu_name: payload.spu_name,
      skc_info_list: list.slice(i, i + maxSkc),
    });
  }
  return chunks;
}
